import {
  LocalParticipant,
  createLocalVideoTrack,
  createLocalAudioTrack,
  CreateLocalTrackOptions
} from "twilio-video";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setErrorNotification } from "store/errors/slice";
import { ErrorType } from "store/errors/types";
import { RootState } from "store/reducers";
import {
  setBusy,
  setLocalAudioTrack,
  setLocalVideoTrack,
} from "store/local-tracks/slice";
import { findAudioTrack, getBrowser } from "utils";
import { isIOS } from "react-device-detect";
import { NetworkQuality } from "store/user/types";
import rollbar from "helpers/rollbar";

export function useLocalTracks(participant: LocalParticipant) {
  const dispatch = useDispatch();
  const {
    localVideoTrack,
    localAudioTrack,
    hideVideo,
    muteAudio,
    videoTrackInfo,
    audioTrackInfo,
    showOnlyLocalVideo,
  } = useSelector((s: RootState) => s.localTracks);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLVideoElement>(null);
  const { networkQuality } = useSelector((s: RootState) => s.user);

  /** Create, attach, and publish a new local video track when
   * localTracks.videoTrackInfo changes. This happens
   * when the user updates the input while in the room. */
  useEffect(() => {
    async function attachNewVideo() {
      if (videoTrackInfo && localVideoTrack && videoRef.current) {
        if (videoTrackInfo?.name !== localVideoTrack.name) {
          let options: CreateLocalTrackOptions = {
            name: videoTrackInfo?.name,
            deviceId: videoTrackInfo?.deviceId,
            aspectRatio: 1.7777777778,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          };

          if (networkQuality === NetworkQuality.Fair) {
            options.width = { ideal: 854 };
            options.height = { ideal: 480 };
          }

          if (networkQuality === NetworkQuality.Poor) {
            options.width = { ideal: 640 };
            options.height = { ideal: 360 };
          }

          try {
            dispatch(setBusy({ video: true }));
            localVideoTrack.stop();
            localVideoTrack.detach();

            participant.unpublishTrack(localVideoTrack);

            if (/Firefox/.test(getBrowser().name || "")) {
              await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: videoTrackInfo?.deviceId } },
              });
            }

            const newVideoTrack = await createLocalVideoTrack(options);

            if (hideVideo) {
              newVideoTrack.disable();
            }
            dispatch(setLocalVideoTrack(newVideoTrack));
            await participant.publishTrack(newVideoTrack);
          } catch (err) {
            dispatch(
              setErrorNotification({
                name: err.name,
                type: ErrorType.MediaError,
                message: err.message,
                showDismiss: true,
              })
            );
            rollbar.error(err, { videoTrackInfo: options });
          } finally {
            dispatch(setBusy({ video: false }));
          }
        }
      }
    }

    attachNewVideo();
  }, [
    dispatch,
    videoTrackInfo,
    hideVideo,
    localVideoTrack,
    networkQuality,
    participant,
  ]);

  /** Create, attach, and publish a new local audio track when
   * localTracks.audioTrackInfo changes. */
  useEffect(() => {
    async function attachNewAudio() {
      const audioTrack = findAudioTrack(participant.audioTracks);

      if (
        audioTrackInfo?.deviceId &&
        audioTrackInfo.label !== audioTrack?.mediaStreamTrack.label
      ) {
        try {
          dispatch(setBusy({ audio: true }));
          if (audioTrack) {
            audioTrack.stop();
            audioTrack.detach();

            const newAudioTrack = await createLocalAudioTrack({
              deviceId: audioTrackInfo.deviceId,
            });

            if (muteAudio) {
              newAudioTrack.disable();
            }

            participant.unpublishTrack(audioTrack);
            await participant.publishTrack(newAudioTrack);

            dispatch(setLocalAudioTrack(newAudioTrack));
          }
        } catch (err) {
          dispatch(
            setErrorNotification({
              name: err.name,
              type: ErrorType.MediaError,
              message: err.message,
              showDismiss: true,
            })
          );
          rollbar.error(err, { audioTrackInfo: audioTrackInfo });
        } finally {
          dispatch(setBusy({ audio: false }));
        }
      }
    }

    attachNewAudio();
  }, [dispatch, audioTrackInfo, muteAudio, participant]);

  useEffect(() => {
    if (localVideoTrack && videoRef.current) {
      if (hideVideo && localVideoTrack.isEnabled) {
        localVideoTrack.disable();
      }

      if (!hideVideo && !localVideoTrack.isEnabled) {
        localVideoTrack.enable(true);

        console.log(localVideoTrack, "color:blue;")
        if (isIOS) {
          // BS workaround for twilio-video-js / safari
            dispatch(setBusy({ video: true }));
            localVideoTrack.restart()
              .finally(() => dispatch(setBusy({ video: false })));
        }
      }
    }
  }, [dispatch, hideVideo, localVideoTrack]);

  useEffect(() => {
    if (localAudioTrack) {
      if (muteAudio && localAudioTrack.isEnabled) {
        localAudioTrack.disable();
      }
      if (!muteAudio && !localAudioTrack.isEnabled) {
        localAudioTrack.enable();
      }
    }
  }, [muteAudio, localAudioTrack]);

  useEffect(() => {
    async function attach() {
      if (localVideoTrack && videoRef.current) {
        if (localVideoTrack.isStopped) {
          await localVideoTrack.restart();
        }
  
        localVideoTrack.attach(videoRef.current);
      }
    }

    attach();

    return () => {
      localVideoTrack?.detach();
    };
  }, [localVideoTrack]);

  useEffect(() => {
    async function attach() {
      if (localAudioTrack && audioRef.current) {
        if (localAudioTrack.isStopped) {
          await localAudioTrack.restart();
        }
  
        localAudioTrack.attach(audioRef.current);
      }
    }

    attach();

    return () => {
      localAudioTrack?.detach();
    };
  }, [localAudioTrack]);

  return { videoRef, audioRef, muteAudio, videoTrackInfo, showOnlyLocalVideo };
}
